// Project status constants
export const PROJECT_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected'
};

// Project status options for dropdowns and filters
export const PROJECT_STATUS_OPTIONS = [
  { 
    value: PROJECT_STATUS.PENDING, 
    label: 'Pending', 
    color: 'bg-yellow-100 text-yellow-800'
  },
  { 
    value: PROJECT_STATUS.APPROVED, 
    label: 'Approved', 
    color: 'bg-green-100 text-green-800'
  },
  { 
    value: PROJECT_STATUS.REJECTED, 
    label: 'Rejected', 
    color: 'bg-red-100 text-red-800'
  }
];

// Status options for admin status dropdown (excluding pending)
export const ADMIN_STATUS_CHANGE_OPTIONS = [
  { 
    value: PROJECT_STATUS.APPROVED, 
    label: 'Approved', 
    color: 'bg-green-100 text-green-800'
  },
  { 
    value: PROJECT_STATUS.REJECTED, 
    label: 'Rejected', 
    color: 'bg-red-100 text-red-800'
  }
];

// Project actions for admin
export const PROJECT_ACTIONS = {
  APPROVE: 'approve',
  REJECT: 'reject',
  VIEW_DETAILS: 'view_details',
  CHANGE_STATUS: 'change_status'
};

// Form validation limits - UPDATED FOR LAKHS
export const PROJECT_VALIDATION_LIMITS = {
  PROJECT_NAME_MIN_LENGTH: 3,
  PROJECT_NAME_MAX_LENGTH: 200,
  BRIEF_MIN_LENGTH: 10,
  BRIEF_MAX_LENGTH: 1000,
  FINANCIAL_REQUIREMENT_MIN: 0.01, // Minimum 0.01 lakh
  FINANCIAL_REQUIREMENT_MAX: 99999, // Maximum 99,999 lakhs (999 crores)
  APPROVED_AMOUNT_MIN: 0.01, // Minimum 0.01 lakh
  APPROVED_AMOUNT_MAX: 99999, // Maximum 99,999 lakhs (999 crores)
  ALLOCATED_AMOUNT_MIN: 0,
  ALLOCATED_AMOUNT_MAX: 99999, // Maximum 99,999 lakhs (999 crores)
  UO_NUMBER_MIN_LENGTH: 1,
  UO_NUMBER_MAX_LENGTH: 50,
  // NEW: Priority validation limits
  PRIORITY_MIN: 1,
  PRIORITY_MAX: 999
};

// Clearance type options
export const CLEARANCE_TYPE_OPTIONS = [
  { value: 'negotiated_loan', label: 'Negotiated Loan' },
  { value: 'state_plan', label: 'State Plan' }
];

// Clearance status for tracking
export const CLEARANCE_STATUS = {
  PENDING: 'pending',
  CLEARED: 'cleared',
  PARTIAL: 'partial'
};

// Default project data structure
export const DEFAULT_PROJECT_DATA = {
  sector: '',
  department: '',
  projectName: '',
  financialRequirement: '', // In lakhs
  brief: '',
  financialYear: '', // Auto-calculated
  status: PROJECT_STATUS.PENDING,
  approvedAmount: null, // In lakhs - Set by admin during approval
  priority: null, // NEW: Priority field (1 = highest priority)
  
  // Clearance fields
  clearanceType: '',
  allocatedAmount: null, // In lakhs
  uoNumber: '',
  clearanceDate: null,
  clearanceStatus: CLEARANCE_STATUS.PENDING,
  
  // Metadata
  createdAt: null,
  updatedAt: null,
  createdBy: null,
  approvedBy: null,
  approvedAt: null,
  clearanceUpdatedAt: null,
  clearanceUpdatedBy: null,
  priorityUpdatedAt: null, // NEW: Priority update timestamp
  priorityUpdatedBy: null  // NEW: Priority update user
};

// Filter options for project approval page
export const PROJECT_FILTER_OPTIONS = {
  status: [
    { value: '', label: 'All Status' },
    ...PROJECT_STATUS_OPTIONS.map(status => ({
      value: status.value,
      label: status.label
    }))
  ],
  sort: [
    { value: 'createdAt-desc', label: 'Recently Created' },
    { value: 'createdAt-asc', label: 'Oldest First' },
    { value: 'projectName-asc', label: 'Project Name A-Z' },
    { value: 'projectName-desc', label: 'Project Name Z-A' },
    { value: 'financialRequirement-desc', label: 'Highest Amount' },
    { value: 'financialRequirement-asc', label: 'Lowest Amount' },
    { value: 'financialYear-desc', label: 'Latest Financial Year' },
    { value: 'financialYear-asc', label: 'Earliest Financial Year' },
    { value: 'priority-asc', label: 'Priority (Highest First)' }, // NEW
    { value: 'priority-desc', label: 'Priority (Lowest First)' }  // NEW
  ]
};

// Helper function to get status badge configuration
export const getProjectStatusConfig = (status) => {
  return PROJECT_STATUS_OPTIONS.find(option => option.value === status) || 
         { value: status, label: status, color: 'bg-gray-100 text-gray-800' };
};

// SIMPLIFIED FORMATTING - NO CONVERSION, DIRECT DISPLAY
export const formatFinancialRequirement = (amount) => {
  if (!amount && amount !== 0) return 'Not specified';
  
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) return 'Invalid amount';
  
  // Direct display in lakhs with proper formatting
  return `₹${numAmount.toLocaleString('en-IN', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  })}`;
};

// SIMPLIFIED FORMATTING - NO CONVERSION, DIRECT DISPLAY
export const formatClearanceAmount = (amount) => {
  if (!amount && amount !== 0) return '₹0.00';
  
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) return '₹0.00';
  
  // Direct display in lakhs with proper formatting
  return `₹${numAmount.toLocaleString('en-IN', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  })}`;
};

// NEW: Format priority for display
export const formatPriority = (priority) => {
  if (!priority && priority !== 0) return '—';
  const numPriority = parseInt(priority);
  if (isNaN(numPriority)) return '—';
  return numPriority.toString();
};

// Calculate Indian Financial Year based on date - NEXT FINANCIAL YEAR LOGIC
export const calculateFinancialYear = (date = new Date()) => {
  const inputDate = date instanceof Date ? date : new Date(date);
  const year = inputDate.getFullYear();
  const month = inputDate.getMonth() + 1; // getMonth() returns 0-11
  
  // Indian Financial Year: April 1 to March 31
  // NEW LOGIC: Always assign to NEXT financial year
  if (month >= 4) {
    // April to December - assign to NEXT FY (e.g., April 2025 = FY 2026-27)
    return `${year + 1}-${String(year + 2).slice(-2)}`;
  } else {
    // January to March - assign to NEXT FY (e.g., March 2026 = FY 2026-27)
    return `${year}-${String(year + 1).slice(-2)}`;
  }
};

// Get full financial year description
export const getFinancialYearDescription = (fyCode) => {
  if (!fyCode) return 'Not specified';
  
  const [startYear, endYearShort] = fyCode.split('-');
  const endYear = startYear.slice(0, 2) + endYearShort;
  
  return `FY ${fyCode} (Apr ${startYear} - Mar ${endYear})`;
};

// UPDATED VALIDATION FOR LAKHS
export const validateApprovedAmount = (amount, requiredAmount = null) => {
  if (!amount || amount.toString().trim() === '') {
    return 'Approved amount is required';
  }
  
  const numAmount = parseFloat(amount);
  
  if (isNaN(numAmount)) {
    return 'Please enter a valid approved amount';
  }
  
  if (numAmount < PROJECT_VALIDATION_LIMITS.APPROVED_AMOUNT_MIN) {
    return 'Approved amount must be greater than 0';
  }
  
  if (numAmount > PROJECT_VALIDATION_LIMITS.APPROVED_AMOUNT_MAX) {
    return 'Approved amount cannot exceed ₹99,999 lakhs';
  }
  
  return null;
};